/**
 * Confirmação de exclusão de um título manual.
 *
 * Excluir contraria o princípio da migração 0012 ("sem delete: parcela some do
 * relatório sem deixar rastro"), e a exceção tem um motivo estreito: a linha
 * que nunca teve baixa e nunca veio de documento não é histórico, é digitação.
 * Manter como "cancelado para sempre" sujaria o relatório de inadimplência com
 * uma linha que nunca foi dívida.
 *
 * Por isso o diálogo diz em texto POR QUE a exclusão é possível aqui — e o
 * servidor é quem impõe as duas condições de novo. A tela esconde o botão por
 * conveniência; quem decide é o serviço (CONSTITUICAO.md §4.1).
 */
import { useState, type FormEvent } from 'react'

import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { excluirConta, type Lado, type LinhaConta } from '../../plataforma/financeiro'
import { dataISOparaBR } from '../../plataforma/mascaras'
import { Dialogo, RodapeDoDialogo } from '../../componentes/Dialogo'

export function DialogoDeExclusao({
  lado,
  linha,
  aoFechar,
  aoConcluir,
}: {
  lado: Lado
  linha: LinhaConta
  aoFechar: () => void
  aoConcluir: (aviso: string) => void
}) {
  const [erro, setErro] = useState('')
  const [excluindo, setExcluindo] = useState(false)

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setErro('')
    setExcluindo(true)
    try {
      await excluirConta(lado, linha.codigo)
      aoConcluir(`Título ${linha.numero_documento} excluído.`)
    } catch (causa: unknown) {
      setErro(
        causa instanceof ErroApi ? causa.message : 'Não foi possível excluir o título.',
      )
      setExcluindo(false)
    }
  }

  return (
    <Dialogo
      icone="warning"
      titulo={`Excluir ${linha.numero_documento}?`}
      descricao="Some da listagem e das somas. Não dá para desfazer."
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} noValidate>
        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3">
          <p className="text-[13.5px] font-semibold text-tinta">{linha.descricao}</p>
          <p className="tabular mt-1 text-[12.5px] text-texto-suave">
            {formatarMoeda(linha.valor_centavos)} · vence em {dataISOparaBR(linha.vencimento)}
          </p>
        </div>

        <p className="mt-3 text-[12.5px] leading-[1.55] text-texto-suave">
          Dá para excluir porque este título foi lançado à mão e nunca teve baixa. Título que veio
          de venda ou de nota fiscal não se exclui — corrige-se o documento; e título já{' '}
          {lado === 'receber' ? 'recebido' : 'pago'} vira histórico, não some.
        </p>

        {erro && (
          <p
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] font-medium text-erro-texto"
          >
            <Icone nome="error" tamanho={16} className="mt-px shrink-0" />
            <span>{erro}</span>
          </p>
        )}

        <RodapeDoDialogo
          confirmar="Excluir título"
          confirmando="Excluindo…"
          ocupado={excluindo}
          destrutivo
          aoCancelar={aoFechar}
        />
      </form>
    </Dialogo>
  )
}
